import {
  AttentionBlock,
  Checkbox,
  ChipToggle,
  ChoiceRow,
  Field,
  FieldGrid,
  Input,
  Select,
} from '@westhetics/ui'
import type { PaymentMethod, PaymentTerms } from './api'
import { PAYMENT_METHOD_LABELS, paymentTermsText, withMethods } from './payment-terms'

/**
 * A condição de pagamento estruturada (RN-ADM-32) com a frase gerada ao lado: "No contrato sai: …".
 * A frase não é digitada; muda sozinha a cada escolha e é igual em todo documento.
 */

const METHODS: PaymentMethod[] = ['pix', 'debit', 'credit', 'cash']

export interface PaymentTermsErrors {
  methods?: string
  creditInstallments?: string
  upfrontDiscount?: string
}

export function PaymentTermsFields({
  value,
  onChange,
  errors = {},
  discountText,
  onDiscountText,
}: {
  value: PaymentTerms
  onChange: (terms: PaymentTerms) => void
  errors?: PaymentTermsErrors
  /** O percentual como a pessoa digita ("7,5"); o número fica em `value.upfrontDiscount`. */
  discountText: string
  onDiscountText: (text: string) => void
}) {
  const hasCredit = value.methods.includes('credit')
  const discountEligible = value.methods.filter((method) => method !== 'credit')
  const preview = paymentTermsText(value)

  function toggleMethod(method: PaymentMethod, pressed: boolean) {
    const methods = pressed
      ? [...value.methods, method]
      : value.methods.filter((item) => item !== method)
    onChange(withMethods(value, methods))
  }

  function setDiscount(text: string, methods: PaymentMethod[]) {
    onDiscountText(text)
    const percent = Number(text.replace('%', '').replace(',', '.').trim())
    const valid = text.trim() !== '' && Number.isFinite(percent) && percent > 0 && percent <= 100
    onChange({
      ...value,
      upfrontDiscount:
        valid && methods.length > 0 ? { percent: Math.round(percent * 10) / 10, methods } : null,
    })
  }

  const discountMethods = value.upfrontDiscount?.methods ?? (discountText ? [] : [])

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="eyebrow" id="payment-methods-label">
          Condição de pagamento
        </div>
        <div
          role="group"
          aria-labelledby="payment-methods-label"
          className="mt-[6px] flex flex-wrap gap-2"
        >
          {METHODS.map((method) => (
            <ChipToggle
              key={method}
              pressed={value.methods.includes(method)}
              onPressedChange={(pressed) => toggleMethod(method, pressed)}
            >
              {PAYMENT_METHOD_LABELS[method]}
            </ChipToggle>
          ))}
        </div>
        {errors.methods ? (
          <p
            role="alert"
            className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
          >
            {errors.methods}
          </p>
        ) : null}
      </div>

      {hasCredit ? (
        <FieldGrid>
          <Field label="Parcelas no crédito" error={errors.creditInstallments}>
            <Select
              value={String(value.creditInstallments ?? 1)}
              onChange={(event) =>
                onChange({ ...value, creditInstallments: Number(event.target.value) })
              }
            >
              {Array.from({ length: 24 }, (_, index) => index + 1).map((count) => (
                <option key={count} value={count}>
                  {count === 1 ? 'à vista' : `até ${count}×`}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-end">
            <ChoiceRow
              className="w-full"
              control={
                <Checkbox
                  checked={value.isInterestFree ?? true}
                  onCheckedChange={(checked) =>
                    onChange({ ...value, isInterestFree: checked === true })
                  }
                />
              }
            >
              Sem juros
            </ChoiceRow>
          </div>
        </FieldGrid>
      ) : null}

      {discountEligible.length > 0 ? (
        <FieldGrid>
          <Field
            label="Desconto à vista"
            optional
            error={errors.upfrontDiscount}
            hint="Em porcentagem, como 10 ou 7,5."
          >
            <Input
              inputMode="decimal"
              placeholder="0"
              value={discountText}
              onChange={(event) =>
                setDiscount(
                  event.target.value,
                  discountMethods.length > 0 ? discountMethods : [discountEligible[0]!],
                )
              }
            />
          </Field>
          {discountText.trim() !== '' ? (
            <div>
              <div className="eyebrow" id="discount-methods-label">
                Vale para
              </div>
              <div
                role="group"
                aria-labelledby="discount-methods-label"
                className="mt-[6px] flex flex-wrap gap-2"
              >
                {discountEligible.map((method) => (
                  <ChipToggle
                    key={method}
                    pressed={discountMethods.includes(method)}
                    onPressedChange={(pressed) =>
                      setDiscount(
                        discountText,
                        pressed
                          ? [...discountMethods, method]
                          : discountMethods.filter((item) => item !== method),
                      )
                    }
                  >
                    {PAYMENT_METHOD_LABELS[method]}
                  </ChipToggle>
                ))}
              </div>
            </div>
          ) : null}
        </FieldGrid>
      ) : null}

      <AttentionBlock variant="lilac" tone="pampas" title="No contrato sai">
        {preview ? `"${preview}"` : 'Escolha os meios de pagamento para ver a frase do contrato.'}
      </AttentionBlock>
    </div>
  )
}
